import {
  Money04Icon,
  ArrowLeftRightIcon,
  Edit02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { useAuthContext } from "providers/Auth";
import { Button, ButtonVariant, ButtonSize } from "shared/components";
import { MenuRow } from "views/applications-id/components/LayoutHeader/components/MenuRow";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import {
  BankStatements,
  EditIncomeModalData,
  EditTransactionsDrawerData
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/types";
import { CombinedIncome } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import { VerifiedItem } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified";
import {
  getIncomeActualNetAndGrossDollars,
  isValidBankStatement
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/utils";
import { useMemo } from "react";
import { DocumentVerificationType } from "shared/types";

const EditButton = ({
  isDisabled,
  onClick
}: {
  isDisabled: boolean;
  onClick?: () => void;
}) => (
  <Button
    variant={ButtonVariant.BASE}
    size={ButtonSize.SM}
    HugeIcon={Edit02Icon}
    className=""
    disabled={isDisabled}
    tooltip={
      isDisabled ? "You don't have permission for this action" : undefined
    }
    onClick={onClick}
  >
    Edit
  </Button>
);

export const EditIncomeDropdownMenu = ({
  setShowEditIncomeAmountModal,
  setShowEditIncomeTransactionsDrawer,
  currentItem,
  income,
  allBankStatements
}: {
  setShowEditIncomeAmountModal?: (data: false | EditIncomeModalData) => void;
  setShowEditIncomeTransactionsDrawer?: (
    data: false | EditTransactionsDrawerData
  ) => void;
  currentItem?: VerifiedItem | VerifiedItem[] | undefined;
  income: CombinedIncome;
  allBankStatements?: BankStatements;
}) => {
  const { permissions } = useAuthContext();
  const { actualVerifiedNetMonthlyDollars, actualVerifiedGrossMonthlyDollars } =
    getIncomeActualNetAndGrossDollars(income, currentItem);

  const isCurrentItemArchived = Array.isArray(currentItem)
    ? currentItem.every((item) => item.is_archived)
    : (currentItem?.is_archived ?? false);

  const filteredBankStatements = useMemo(
    () =>
      allBankStatements
        ? Object.values(allBankStatements).filter((bankStatement) =>
            isValidBankStatement(bankStatement)
          )
        : [],
    [allBankStatements]
  );
  const isCurrentlySelectedIncomeItemABankStatement =
    Array.isArray(currentItem) &&
    currentItem[0]?.income_source_type ===
      DocumentVerificationType.BankStatement;

  const isTransactionsEditAvailable = Boolean(
    !isCurrentItemArchived &&
    isCurrentlySelectedIncomeItemABankStatement &&
    filteredBankStatements?.length
  );

  if (!isTransactionsEditAvailable)
    return (
      <EditButton
        isDisabled={!permissions.canEditIncome}
        onClick={() =>
          setShowEditIncomeAmountModal?.({
            grossMonthlyIncomeDollars: actualVerifiedGrossMonthlyDollars,
            netMonthlyIncomeDollars: actualVerifiedNetMonthlyDollars,
            hasNetMonthlyIncome: income.type !== "offer_letter",
            isTotal: false,
            id: income.id,
            additional_data: income.additional_data,
            type: income.type
          })
        }
      />
    );

  const dropdownActions = [
    {
      title: <MenuRow text="Edit income amount" HugeIcon={Money04Icon} />,
      className: "!py-2.5",
      disabled: !permissions.canEditIncome,
      tooltip: permissions.canEditIncome
        ? undefined
        : "You don't have permission for this action",
      onClick: () =>
        setShowEditIncomeAmountModal?.({
          grossMonthlyIncomeDollars: actualVerifiedGrossMonthlyDollars,
          netMonthlyIncomeDollars: actualVerifiedNetMonthlyDollars,
          hasNetMonthlyIncome: income.type !== "offer_letter",
          isTotal: false,
          id: income.id,
          additional_data: income.additional_data,
          type: income.type
        })
    },
    {
      title: (
        <MenuRow
          text="Edit income transacitons"
          HugeIcon={ArrowLeftRightIcon}
        />
      ),
      className: "!py-2.5",

      disabled: !permissions.canEditIncome,
      tooltip: permissions.canEditIncome
        ? undefined
        : "You don't have permission for this action",
      onClick: () =>
        setShowEditIncomeTransactionsDrawer?.({
          id: income.id,
          type: income.type,
          additional_data: income.additional_data
        })
    }
  ];

  return (
    <ContextMenuPopover
      borderLess={false}
      trigger={<EditButton isDisabled={!permissions.canEditIncome} />}
      actions={dropdownActions}
      placement="bottom-end"
      contentClassName="w-[275px]"
    />
  );
};
